import { createSlice } from '@reduxjs/toolkit';
import AdminService from 'views/v2/services/AdminService';
import DocumentRequestService from 'views/v2/services/documentService';
import orderservice from 'views/v2/services/orderservice';

const entityCountAllowedRoles = ["Admin", "SuperAdmin"];
const isPatientOrProfessional = ["Patient", "CompanyAdmin", "CompanyEmployee"];

const fetchRequesterOrderCounts = async () => {
  try {
    const response = await DocumentRequestService.getRequesterCountsByStatus();
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error in fetchRequesterOrderCounts:", error);
    return null;
  }
};

const fetchRequestProcessorCountsByStatus = async () => {
  try {
    const response = await DocumentRequestService.getRequestProcessorCountsByStatus();
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error in fetchRequestProcessorCountsByStatus:", error);
    return null;
  }
};

const fetchAdminEntityCounts = async () => {
  try {
    const response = await AdminService.getPendingFacilityCounts();
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error in fetchAdminEntityCounts:", error);
    return null;
  }
};

const fetchRequesterTicketsCounts = async () => {
  try {
    const response = await DocumentRequestService.getRequesterTicketsCounts();
    return response?.data?.data?.[0] || null;
  } catch (error) {
    console.error("Error in fetchRequesterTicketsCounts:", error);
    return null;
  }
};

const fetchDraftsCount = async (requesterAccountId) => {
  if (!requesterAccountId) return 0;

  try {
    const response = await orderservice.getDraftRequestProcessorOrders(requesterAccountId, {
      pageNumber: 1,
      perPage: 1,
    });

    const count =
      response?.total ??
      response?.count ??
      response?.data?.total ??
      response?.data?.count ??
      response?.totalCount ??
      response?.data?.totalCount ??
      response?.length ??
      response?.data?.length ??
      0;

    return Number(count) || 0;
  } catch (error) {
    console.error("Error in fetchDraftsCount:", error);
    return 0;
  }
};

const fetchProcessorTicketsCounts = async () => {
  try {
    const response = await DocumentRequestService.getRequestProcessorTicketsCounts();
    return response?.data?.data?.[0] || null;
  } catch (error) {
    console.error("Error in fetchProcessorTicketsCounts:", error);
    return null;
  }
};

const fetchPendingUpdatesSummary = async () => {
  try {
    const response = await AdminService.countPendingMasterDataReleases();
    const payload = response?.data?.data || response?.data || {};
    return {
      count: Number(payload.count) || 0,
      latest: payload.latest || null,
    };
  } catch (error) {
    console.error("Error in fetchPendingUpdatesSummary:", error);
    return { count: 0, latest: null };
  }
};

const sideNavCountsSlice = createSlice({
  name: 'sideNavCounts',

  initialState: {
    requesterResponse: null,
    requesterProcessorResponse: null,
    superAdminCount: null,
    tickets: {count: 0},
    drafts: {count: 0},
    pendingUpdates: { count: 0, latest: null },
    pendingUpdatesReady: false,
  },

  reducers: {
    setCounts: (state, action) => {
      state.requesterResponse = action.payload.requesterResponse ?? state.requesterResponse;
      state.requesterProcessorResponse = action.payload.requesterProcessorResponse ?? state.requesterProcessorResponse;
      state.superAdminCount = action.payload.superAdminCount ?? state.superAdminCount;
      state.tickets = action.payload.tickets ?? state.tickets;
      state.drafts = action.payload.drafts ?? state.drafts;
      state.pendingUpdates = action.payload.pendingUpdates ?? state.pendingUpdates;
      if (action.payload.pendingUpdatesReady != null) {
        state.pendingUpdatesReady = action.payload.pendingUpdatesReady;
      } else if (action.payload.pendingUpdates !== undefined) {
        state.pendingUpdatesReady = true;
      }
    },

    resetCounts: (state) => {
      state.requesterResponse = null;
      state.requesterProcessorResponse = null;
      state.superAdminCount = null;
      state.tickets = {count: 0};
      state.drafts = {count: 0};
      state.pendingUpdates = { count: 0, latest: null };
      state.pendingUpdatesReady = false;
    },
  },
});

export const { setCounts, resetCounts } = sideNavCountsSlice.actions;

export const refetchCounts = (userRole) => async (dispatch, getState) => {
  let counts = {};

  if (isPatientOrProfessional.includes(userRole)) {
    const requesterResponse = await fetchRequesterOrderCounts();
    const tickets = await fetchRequesterTicketsCounts();
    const drafts = { count: await fetchDraftsCount(getState()?.auth?.user?.accountId) };
    counts = { requesterResponse, tickets, drafts, pendingUpdatesReady: true };


  } else if (entityCountAllowedRoles.includes(userRole)) {
    const superAdminCount = await fetchAdminEntityCounts();
    counts = { superAdminCount, pendingUpdatesReady: true };

  } else {
    const requesterProcessorResponse = await fetchRequestProcessorCountsByStatus();
    const tickets = await fetchProcessorTicketsCounts();
    const pendingUpdates = await fetchPendingUpdatesSummary();
    counts = {
      requesterProcessorResponse,
      tickets,
      pendingUpdates,
      pendingUpdatesReady: true,
    };
  }

  dispatch(setCounts(counts));
};

export default sideNavCountsSlice.reducer;
